import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, authHeader, API } from "../lib/api";
import { useGame } from "../context/GameContext";
import { useCompositeRecorder } from "../hooks/useCompositeRecorder";
import { RECORD_COMPOSITE_LIVE, COMPOSITE_RECORDER_BITRATES } from "../lib/videoCapture";
import { unlockAudioContext, playAudioArrayBuffer, primeAudioForPlayback } from "../lib/playAudio";
import CameraPanView, { CAMERA_PAN_MS } from "../components/CameraPanView";
import CandidateSilhouetteGuide from "../components/CandidateSilhouetteGuide";
import { Circle, Loader2, Mic } from "lucide-react";
import "../play.css";

const API_BASE = API;
const PAN_MS = CAMERA_PAN_MS;
const POST_QUESTION_DELAY_MS = 1500;
const END_REDIRECT_DELAY = 1600;

const isPageReload = () => {
  if (typeof performance === "undefined") return false;
  const nav = performance.getEntriesByType?.("navigation")?.[0];
  return nav?.type === "reload";
};

/**
 * Phases :
 *  - "panning"  : flou de déplacement vers la droite (sauf 1ʳᵉ question)
 *  - "audio"    : lecture de la question (si audio)
 *  - "answering": chrono actif, le candidat répond à voix haute
 *  - "between"  : micro-pause avant pan / fin
 */
const Play = () => {
  const { token, setSession, streamRef, setCompositeVideoBlob } = useGame();
  const navigate = useNavigate();
  const playScreenRef = useRef(null);
  const flashWagerRef = useRef({ active: false, startedAt: 0 });
  const compositeRevRef = useRef(0);
  const compositeLiveRef = useRef({});
  const timersRef = useRef([]);
  const sessionIdRef = useRef(null);
  const questionGenerationRef = useRef(0);
  const answeringStartedAtRef = useRef(null);
  const submitLockRef = useRef(false);
  const submitAnswerRef = useRef(() => {});
  const audioElRef = useRef(null);

  const [starting, setStarting] = useState(true);
  const [recordingBitrates, setRecordingBitrates] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [phase, setPhase] = useState("panning");
  const [panning, setPanning] = useState(false);
  const [panKey, setPanKey] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(5);
  const [finishing, setFinishing] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const questionAudioCacheRef = useRef(new Map()); // qid -> { buffer, mimeType }

  const currentQ = questions[currentIdx];
  const thinkTime = currentQ?.think_time_seconds ?? 5;

  const playScreenCallbackRef = useCallback((node) => {
    playScreenRef.current = node;
  }, []);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((t) => {
      clearTimeout(t);
      clearInterval(t);
    });
    timersRef.current = [];
  }, []);

  const schedule = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
    return id;
  }, []);

  useEffect(() => {
    const prev = compositeLiveRef.current || {};
    const panStartedAt = panning
      ? (prev._panArmed ? prev.panStartedAt : Date.now())
      : 0;

    const liveMirrored = true;
    const panStartMirrored = true;
    const panEndMirrored = true;

    compositeLiveRef.current = {
      phase,
      questionText: phase === "answering" || phase === "audio" ? currentQ?.text ?? "" : "",
      showQuestion: phase === "answering" || phase === "audio",
      questionIndex: currentIdx,
      questionCount: questions.length,
      secondsLeft: phase === "answering" ? secondsLeft : null,
      thinkTimeSeconds: thinkTime,
      timerShown: phase === "answering",
      panning,
      panStartedAt,
      panDurationMs: PAN_MS,
      liveMirrored,
      panStartMirrored,
      panEndMirrored,
      _panArmed: panning,
    };
    compositeRevRef.current += 1;
  }, [phase, currentQ, currentIdx, questions.length, secondsLeft, thinkTime, panning]);

  const { stopRecording } = useCompositeRecorder(streamRef, playScreenRef, {
    onBlob: (blob) => setCompositeVideoBlob?.(blob),
    enabled: RECORD_COMPOSITE_LIVE && !starting && Boolean(recordingBitrates),
    flashWagerRef,
    compositeRevRef,
    liveOverlayRef: compositeLiveRef,
    recordingBitrates: recordingBitrates || COMPOSITE_RECORDER_BITRATES,
  });

  const stopQuestionAudio = useCallback(() => {
    const ctrl = audioElRef.current;
    if (ctrl?.abort) {
      try {
        ctrl.abort();
      } catch {
        /* ignore */
      }
    }
    audioElRef.current = null;
  }, []);

  const fetchQuestionAudio = useCallback(
    async (sessionId, question, signal) => {
      if (!question?.has_audio || !token) return null;
      const cached = questionAudioCacheRef.current.get(question.id);
      if (cached) return cached;
      const url = `${API_BASE}/sessions/${sessionId}/questions/${question.id}/audio`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      if (!res.ok) return null;
      const mimeType = res.headers.get("content-type") || "audio/mpeg";
      const arrayBuffer = await res.arrayBuffer();
      if (!arrayBuffer.byteLength) return null;
      const payload = { buffer: arrayBuffer, mimeType };
      questionAudioCacheRef.current.set(question.id, payload);
      return payload;
    },
    [token],
  );

  const playQuestionAudio = useCallback(
    async (sessionId, question) => {
      if (!question?.has_audio || !token) return false;
      stopQuestionAudio();
      await unlockAudioContext();
      const ctrl = new AbortController();
      audioElRef.current = ctrl;

      try {
        const payload = await fetchQuestionAudio(sessionId, question, ctrl.signal);
        if (!payload || ctrl.signal.aborted) {
          if (question?.has_audio) {
            toast.error("Audio de la question introuvable. Ré-uploadez-le dans l’admin.");
          }
          return false;
        }
        await playAudioArrayBuffer(payload.buffer.slice(0), 1, {
          signal: ctrl.signal,
          mimeType: payload.mimeType || "audio/mpeg",
        });
        return !ctrl.signal.aborted;
      } catch (err) {
        if (err?.name === "AbortError") return false;
        return false;
      } finally {
        if (audioElRef.current === ctrl) audioElRef.current = null;
      }
    },
    [stopQuestionAudio, token, fetchQuestionAudio],
  );

  const finishGame = useCallback(async () => {
    setFinishing(true);
    clearTimers();
    stopQuestionAudio();
    try {
      let blob = null;
      if (typeof stopRecording === "function") {
        blob = await stopRecording();
      }
      if (blob?.size) setCompositeVideoBlob?.(blob);
    } catch (err) {
      console.warn("[play] stop recording", err);
    }
    schedule(() => navigate("/thank-you"), END_REDIRECT_DELAY);
  }, [clearTimers, stopQuestionAudio, stopRecording, setCompositeVideoBlob, schedule, navigate]);

  const submitAnswer = useCallback(
    async ({ answered, timedOut }) => {
      if (submitLockRef.current) return;
      submitLockRef.current = true;
      const gen = questionGenerationRef.current;
      const sid = sessionIdRef.current;
      const started = answeringStartedAtRef.current;
      const responseMs = started != null ? Date.now() - started : null;

      clearTimers();
      stopQuestionAudio();

      try {
        const { data } = await api.post(
          `/sessions/${sid}/answer`,
          {
            answered,
            timed_out: timedOut,
            response_ms: responseMs,
          },
          authHeader(token),
        );

        if (gen !== questionGenerationRef.current) return;

        if (data.is_final) {
          await finishGame();
          return;
        }

        setPhase("between");
        const nextIdx = data.next_question_index ?? currentIdx + 1;
        schedule(() => {
          questionGenerationRef.current += 1;
          setCurrentIdx(nextIdx);
          setPanKey((k) => k + 1);
          setPanning(true);
          setPhase("panning");
          submitLockRef.current = false;
        }, 280);
      } catch (err) {
        submitLockRef.current = false;
        toast.error(err?.response?.data?.detail || "Erreur d’enregistrement de la réponse");
      }
    },
    [clearTimers, stopQuestionAudio, finishGame, currentIdx, schedule, token],
  );

  useEffect(() => {
    submitAnswerRef.current = submitAnswer;
  }, [submitAnswer]);

  // Boucle : pan (sauf Q1) → audio → answering
  useEffect(() => {
    if (starting || !currentQ || !sessionIdRef.current) return undefined;
    if (phase !== "panning" && phase !== "audio" && phase !== "answering") return undefined;

    const gen = questionGenerationRef.current;
    let cancelled = false;

    const run = async () => {
      if (phase === "panning") {
        const isFirst = currentIdx === 0;
        // Précharge l'audio pendant le pan (lecture auto dès la phase audio)
        if (currentQ?.has_audio) {
          void fetchQuestionAudio(sessionIdRef.current, currentQ).catch(() => null);
        }
        if (isFirst) {
          setPanning(false);
          await new Promise((r) => {
            const t = setTimeout(r, 350);
            timersRef.current.push(t);
          });
        } else {
          setPanning(true);
          // Laisse le pan + le reset invisible du rail se terminer (évite flash / trou)
          await new Promise((r) => {
            const t = setTimeout(r, PAN_MS + 48);
            timersRef.current.push(t);
          });
        }
        if (cancelled || gen !== questionGenerationRef.current) return;
        setPanning(false);
        setPhase("audio");
        return;
      }

      if (phase === "audio") {
        setShowGuide(false);
        const played = await playQuestionAudio(sessionIdRef.current, currentQ);
        if (cancelled || gen !== questionGenerationRef.current) return;
        // Chrono dès la fin du son. Sinon (pas d’audio / erreur) → délai 1,5 s.
        if (!played) {
          await new Promise((r) => {
            const t = setTimeout(r, POST_QUESTION_DELAY_MS);
            timersRef.current.push(t);
          });
          if (cancelled || gen !== questionGenerationRef.current) return;
        }
        setSecondsLeft(thinkTime);
        answeringStartedAtRef.current = Date.now();
        setPhase("answering");
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [starting, currentQ, phase, currentIdx, thinkTime, playQuestionAudio, fetchQuestionAudio]);

  useEffect(() => {
    if (phase !== "answering" || starting) return undefined;
    const gen = questionGenerationRef.current;
    const tick = () => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          if (gen === questionGenerationRef.current) {
            submitAnswerRef.current({ answered: false, timedOut: true });
          }
          return 0;
        }
        return prev - 1;
      });
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [phase, starting, currentIdx]);

  useEffect(() => {
    const onKey = (e) => {
      if (phase !== "answering") return;
      if (e.code === "Space" || e.code === "Enter") {
        e.preventDefault();
        submitAnswerRef.current({ answered: true, timedOut: false });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase]);

  useLayoutEffect(() => {
    if (!token) {
      navigate("/");
      return undefined;
    }
    if (!streamRef?.current) {
      navigate("/camera");
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        await primeAudioForPlayback();
        const headers = isPageReload() ? { ...authHeader(token).headers, "X-Play-Reload": "1" } : undefined;
        const { data } = await api.post(
          "/sessions",
          {},
          headers ? { headers } : authHeader(token),
        );
        if (cancelled) return;
        sessionIdRef.current = data.session_id;
        setSession?.({ session_id: data.session_id, code: data.code });
        setQuestions(data.questions || []);
        setCurrentIdx(data.current_question_index || 0);
        setRecordingBitrates(data.recording_bitrates || COMPOSITE_RECORDER_BITRATES);
        setPhase("panning");
        setStarting(false);
        // Précharge tous les audios en arrière-plan
        const sid = data.session_id;
        for (const q of data.questions || []) {
          if (q?.has_audio) void fetchQuestionAudio(sid, q).catch(() => null);
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Impossible de démarrer la session");
        navigate("/welcome");
      }
    })();

    return () => {
      cancelled = true;
      clearTimers();
      stopQuestionAudio();
    };
  }, [token, navigate, streamRef, setSession, clearTimers, stopQuestionAudio, fetchQuestionAudio]);

  if (starting) {
    return (
      <div className="mf-play-shell">
        <div className="play-screen play-screen--mf flex items-center justify-center" data-testid="play-loading">
          <Loader2 className="animate-spin text-md-cyan" size={36} />
        </div>
      </div>
    );
  }

  return (
    <div className="mf-play-shell">
      <div
        className="play-screen play-screen--mf"
        ref={playScreenCallbackRef}
        data-testid="play-screen"
      >
      <CameraPanView
        stream={streamRef?.current}
        panning={panning}
        panKey={panKey}
        questionIndex={currentIdx}
      />

      {showGuide && <CandidateSilhouetteGuide />}

      <div className="mf-play-hud">
        <div className="mf-play-top">
          <div className="mf-play-meta">
            <span className="mf-play-badge">Q{currentIdx + 1}/{questions.length}</span>
            {RECORD_COMPOSITE_LIVE && (
              <span className="mf-rec-pill" data-testid="rec-indicator">
                <Circle size={8} className="fill-red-500 text-red-500" /> REC
              </span>
            )}
          </div>

          {(phase === "audio" || phase === "answering") && currentQ && (
            <div className="mf-question-card" data-testid="question-text">
              {currentQ.text}
            </div>
          )}
        </div>

        <div className="mf-play-spacer" />

        {phase === "answering" && (
          <div className="mf-answer-zone" data-testid="answer-zone">
            <div
              className={`mf-dots ${secondsLeft <= 2 ? "mf-dots--urgent" : ""}`}
              data-testid="think-timer"
              role="timer"
              aria-label={`${secondsLeft} seconde${secondsLeft > 1 ? "s" : ""} restante${secondsLeft > 1 ? "s" : ""}`}
            >
              {Array.from({ length: thinkTime }, (_, i) => {
                const spent = thinkTime - secondsLeft;
                const faded = i < spent;
                return (
                  <span
                    key={i}
                    className={`mf-dot ${faded ? "mf-dot--faded" : ""}`}
                  />
                );
              })}
            </div>
            <p className="mf-answer-hint">
              <Mic size={16} className="inline mr-1 opacity-70" />
              Répondez à voix haute, puis validez
            </p>
            <button
              type="button"
              className="mf-answer-btn"
              data-testid="answer-submit"
              onClick={() => submitAnswerRef.current({ answered: true, timedOut: false })}
              disabled={finishing}
            >
              J&apos;ai répondu
              <kbd className="mf-kbd">Espace</kbd>
            </button>
          </div>
        )}

        {finishing && (
          <div className="mf-uploading">
            <Loader2 className="animate-spin" size={22} />
            Finalisation…
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Play;
